/**
 * useCalendarActions Hook
 *
 * Writes calendar changes (blocks, custom pricing, min nights) to Firestore.
 *
 * Data model:
 * - listings/{id}/blockedDates/manual_{YYYY-MM-DD}: one doc per manually
 *   blocked night. endDate is exclusive (checkout-style), matching the
 *   server overlap check in createBooking and the iCal sync docs.
 * - listings/{id}/customPricing/{YYYY-MM-DD}: per-date overrides
 *   { price?, minNights? }. Deterministic IDs make set/clear idempotent.
 */

import { useState, useCallback } from 'react';
import {
  doc,
  writeBatch,
  serverTimestamp,
  Timestamp,
  deleteField,
} from 'firebase/firestore';
import { db } from '../../../../services/firebase';
import { formatDate } from '../../../../utils/dates';

export interface CalendarUpdate {
  listingId: string;
  dates: Date[];
  action: 'block' | 'unblock' | 'setPrice' | 'setMinNights';
  value?: number;
  reason?: string;
}

const MANUAL_BLOCK_PREFIX = 'manual_';

function dateKey(date: Date): string {
  return formatDate(date, 'numeric');
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export const useCalendarActions = (listingId: string) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const blockDates = useCallback(
    async (dates: Date[], reason?: string): Promise<boolean> => {
      if (!listingId || dates.length === 0) return false;
      setIsUpdating(true);
      setError(null);

      try {
        const batch = writeBatch(db);
        for (const date of dates) {
          const night = startOfDay(date);
          const nextDay = new Date(night);
          nextDay.setDate(nextDay.getDate() + 1);

          const ref = doc(
            db,
            'listings',
            listingId,
            'blockedDates',
            MANUAL_BLOCK_PREFIX + dateKey(night)
          );
          batch.set(ref, {
            startDate: Timestamp.fromDate(night),
            endDate: Timestamp.fromDate(nextDay), // exclusive
            reason: 'manual',
            note: reason || null,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
        }
        await batch.commit();
        return true;
      } catch (err) {
        console.error('Failed to block dates:', err);
        setError('Failed to block dates');
        return false;
      } finally {
        setIsUpdating(false);
      }
    },
    [listingId]
  );

  const unblockDates = useCallback(
    async (dates: Date[]): Promise<boolean> => {
      if (!listingId || dates.length === 0) return false;
      setIsUpdating(true);
      setError(null);

      try {
        const batch = writeBatch(db);
        for (const date of dates) {
          // Only manual blocks are removable; iCal and booking blocks
          // live under different doc IDs and are never touched here.
          const ref = doc(
            db,
            'listings',
            listingId,
            'blockedDates',
            MANUAL_BLOCK_PREFIX + dateKey(startOfDay(date))
          );
          batch.delete(ref);
        }
        await batch.commit();
        return true;
      } catch (err) {
        console.error('Failed to unblock dates:', err);
        setError('Failed to unblock dates');
        return false;
      } finally {
        setIsUpdating(false);
      }
    },
    [listingId]
  );

  const setCustomPrice = useCallback(
    async (dates: Date[], price: number): Promise<boolean> => {
      if (!listingId || dates.length === 0 || !(price > 0)) return false;
      setIsUpdating(true);
      setError(null);

      try {
        const batch = writeBatch(db);
        for (const date of dates) {
          const ref = doc(
            db,
            'listings',
            listingId,
            'customPricing',
            dateKey(startOfDay(date))
          );
          batch.set(ref, { price, updatedAt: serverTimestamp() }, { merge: true });
        }
        await batch.commit();
        return true;
      } catch (err) {
        console.error('Failed to set custom price:', err);
        setError('Failed to set custom price');
        return false;
      } finally {
        setIsUpdating(false);
      }
    },
    [listingId]
  );

  const clearCustomPrice = useCallback(
    async (dates: Date[]): Promise<boolean> => {
      if (!listingId || dates.length === 0) return false;
      setIsUpdating(true);
      setError(null);

      try {
        const batch = writeBatch(db);
        for (const date of dates) {
          const ref = doc(
            db,
            'listings',
            listingId,
            'customPricing',
            dateKey(startOfDay(date))
          );
          // Keep the doc if a minNights override exists; merge-set keeps
          // this idempotent even when the doc doesn't exist.
          batch.set(
            ref,
            { price: deleteField(), updatedAt: serverTimestamp() },
            { merge: true }
          );
        }
        await batch.commit();
        return true;
      } catch (err) {
        console.error('Failed to clear custom price:', err);
        setError('Failed to clear custom price');
        return false;
      } finally {
        setIsUpdating(false);
      }
    },
    [listingId]
  );

  const setMinNights = useCallback(
    async (dates: Date[], minNights: number): Promise<boolean> => {
      if (!listingId || dates.length === 0 || !(minNights >= 1)) return false;
      setIsUpdating(true);
      setError(null);

      try {
        const batch = writeBatch(db);
        for (const date of dates) {
          const ref = doc(
            db,
            'listings',
            listingId,
            'customPricing',
            dateKey(startOfDay(date))
          );
          batch.set(ref, { minNights, updatedAt: serverTimestamp() }, { merge: true });
        }
        await batch.commit();
        return true;
      } catch (err) {
        console.error('Failed to set minimum nights:', err);
        setError('Failed to set minimum nights');
        return false;
      } finally {
        setIsUpdating(false);
      }
    },
    [listingId]
  );

  return {
    blockDates,
    unblockDates,
    setCustomPrice,
    clearCustomPrice,
    setMinNights,
    isUpdating,
    error,
  };
};

export default useCalendarActions;
